import React from 'react'
import Box from '@material-ui/core/Box'
import LinearProgress from '@material-ui/core/LinearProgress'
import Typography from '@material-ui/core/Typography'
import SectionTitle from 'components/section-title'
import TipWell from 'components/tip-well'
import AttachMoneyIcon from 'components/material-symbol/icons/AttachMoney'
import EmojiObjectsIcon from 'components/material-symbol/icons/EmojiObjects'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { GROWTH_PERIOD_LABELS, WEEKLY_REVENUE_CHART_COLOR } from './constants'
import GrowthChartTooltip from './growth-chart-tooltip'
import { BenchmarkDeltaChip } from 'pages/pga-coach/dashboard/performance-benchmarks/benchmark-chips'
import {
  formatRevenueAmount,
  formatWeekOverWeekDelta,
  getWeeklyRevenueInsight,
  getWeeklyRevenueSummary,
} from './utils'
import useStyles from './styles'

const WeeklyRevenueCard = ({ series, loading, error }) => {
  const classes = useStyles()
  const chartColor = WEEKLY_REVENUE_CHART_COLOR
  const {
    currentWeekTotal,
    weekOverWeekDeltaPercent,
  } = getWeeklyRevenueSummary(series)
  const hasChartData = series.some(point => point.total > 0)
  const insight = getWeeklyRevenueInsight(series)

  return (
    <Box className={classes.card}>
      <Box className={classes.cardHeader}>
        <Box className={classes.cardHeaderLeft}>
          <Box className={classes.iconCircle}>
            <AttachMoneyIcon />
          </Box>
          <Box className={classes.cardTitleBlock}>
            <SectionTitle className={classes.cardTitle}>
              Weekly revenue
            </SectionTitle>
            <Typography className={classes.cardSubtitle}>
              {GROWTH_PERIOD_LABELS.LAST_8_WEEKS}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.cardHeaderRight}>
          <Typography className={classes.revenueTotal}>
            {formatRevenueAmount(currentWeekTotal)}
          </Typography>
          {weekOverWeekDeltaPercent !== null && (
            <BenchmarkDeltaChip
              label={formatWeekOverWeekDelta(weekOverWeekDeltaPercent)}
              positive={weekOverWeekDeltaPercent > 0}
              neutral={weekOverWeekDeltaPercent === 0}
            />
          )}
        </Box>
      </Box>

      <Box className={classes.cardBody}>
        {error ? (
          <Box className={classes.chartError}>
            Unable to load weekly revenue. Please try again later.
          </Box>
        ) : loading ? (
          <LinearProgress color="primary" className={classes.loading} />
        ) : hasChartData ? (
          <Box className={classes.cardMain}>
            <Box className={classes.chartArea}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={series}
                  margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="weeklyRevenueFill"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor={chartColor}
                        stopOpacity={0.25}
                      />
                      <stop
                        offset="95%"
                        stopColor={chartColor}
                        stopOpacity={0.02}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="week"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12 }}
                    width={48}
                    tickFormatter={value =>
                      value >= 1000
                        ? `$${Math.round(value / 1000)}k`
                        : `$${value}`
                    }
                  />
                  <Tooltip
                    content={
                      <GrowthChartTooltip
                        valueFormatter={value => formatRevenueAmount(value)}
                      />
                    }
                    cursor={{
                      stroke: 'rgba(0, 32, 91, 0.12)',
                      strokeWidth: 1,
                      fill: 'rgba(0, 32, 91, 0.04)',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="total"
                    stroke={chartColor}
                    strokeWidth={2}
                    fill="url(#weeklyRevenueFill)"
                    dot={{
                      fill: chartColor,
                      stroke: chartColor,
                      fillOpacity: 0.6,
                      r: 3,
                    }}
                    activeDot={{ r: 5, fill: chartColor, stroke: chartColor }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>

            {insight && (
              <Box className={classes.insightTip}>
                <TipWell
                  variant="promo"
                  icon={EmojiObjectsIcon}
                  copyClassName={classes.insightTipCopy}
                  iconClassName={classes.insightTipIcon}
                  caption={insight}
                />
              </Box>
            )}
          </Box>
        ) : (
          <Box className={classes.chartEmpty}>
            No paid revenue recorded in the last 8 weeks.
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default WeeklyRevenueCard
